import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Chart as ChartJS,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { Diagnostics } from "../../../../../services";
import { POD_COLORS } from "../constants";
import { formatTime } from "../utils/format";

import { EmptyState } from "./Panel";

ChartJS.register(
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	Filler,
);

/**
 * Event loop delay over time.
 *
 * Both series of a pod share its colour: the solid line is the worst block
 * within the tick, the dashed one is the average background. A spike on the
 * solid line alone is a single freeze, while a rising dashed line means the
 * service is degrading as a whole.
 */
const LatencyChart: React.FC<LatencyChart.Props> = ({ pods }) => {
	const { t } = useTranslation();

	const hasData = pods.some((pod) => pod.points.length > 0);

	const data = useMemo(() => {
		const maxLabel = t(
			"settings.systemPerformance.overview.latencyMax",
		) as string;
		const meanLabel = t(
			"settings.systemPerformance.overview.latencyMean",
		) as string;

		const datasets = pods.flatMap((pod, index) => {
			const color = POD_COLORS[index % POD_COLORS.length];
			const name = pod.pod.slice(-12);

			return [
				{
					label: `${maxLabel} · ${name}`,
					borderColor: color,
					backgroundColor: color,
					borderWidth: 2,
					pointRadius: 0,
					tension: 0.25,
					data: pod.points.map((point) => ({
						x: point.ts,
						y: point.eventLoopDelayMax,
					})),
				},
				{
					label: `${meanLabel} · ${name}`,
					borderColor: color,
					backgroundColor: color,
					borderWidth: 1,
					borderDash: [4, 4],
					pointRadius: 0,
					tension: 0.25,
					data: pod.points.map((point) => ({
						x: point.ts,
						y: point.eventLoopDelayMean,
					})),
				},
			];
		});

		return { datasets };
	}, [pods, t]);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animation: false as const,
			interaction: { mode: "nearest" as const, intersect: false },
			scales: {
				x: {
					type: "linear" as const,
					ticks: {
						maxTicksLimit: 10,
						callback: (value: number | string) =>
							formatTime(Number(value)),
					},
				},
				y: {
					type: "linear" as const,
					position: "left" as const,
					title: { display: true, text: "ms" },
					beginAtZero: true,
				},
			},
			plugins: {
				datalabels: { display: false },
				legend: {
					position: "top" as const,
					labels: { boxWidth: 12, boxHeight: 12 },
				},
				tooltip: {
					callbacks: {
						title: (items: any[]) =>
							formatTime(items?.[0]?.parsed?.x),
					},
				},
			},
		}),
		[],
	);

	if (!hasData) {
		return (
			<EmptyState>
				{t("settings.systemPerformance.overview.empty")}
			</EmptyState>
		);
	}

	return (
		<div
			style={{
				height: 280,
				width: "100%",
				maxWidth: "100%",
				minWidth: 0,
			}}
		>
			<Line options={options as any} data={data as any} />
		</div>
	);
};

declare namespace LatencyChart {
	interface Props {
		pods: Diagnostics.TimelineResponse["pods"];
	}
}

export default LatencyChart;
